import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { useOutsideCheckClick } from 'hooks/UseOnClickOutside'
import { useDataIntegrations } from 'components/DataIntegrations/DataIntegrationsProvider'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import { capitalizeString } from 'utils/helpers'
import sentinelV2Api from 'api/sentinelV2'

import { useSentinelChat } from './SentinelChatProvider'
import { downloadTableCSV, downloadTableXLSX } from './exportTable'

const MENU = 'fd-action-menu py-1'
const MENU_STYLE = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-md)',
  minWidth: '160px',
}
const ITEM = 'block w-full text-left fd-body-sm'
const ITEM_STYLE = { padding: '7px 12px', color: 'var(--text-secondary)' }

/**
 * Result action toolbar: Save (⭐, into Saved questions), Export (CSV/Excel,
 * client-side), Send to a connected platform (audiences only), and View SQL.
 * No gold here — gold is reserved for the composer's Ask. Teal/olive never on buttons.
 */
export default function TurnActions({ turn }) {
  const { t } = useTranslation()
  const { conversationId, saveQuestion, saved: savedQuestions } = useSentinelChat()
  const { getSupportedCRMs } = useDataIntegrations()
  const { addNotification } = useToast()

  const table = turn.table || { columns: [], rows: [] }
  const fileName = `Sentinel_${moment().format('YYYY-MM-DD_HH-mm')}`
  const supportedCRMs = turn.isAudience ? getSupportedCRMs() : []

  const [downloadOpen, setDownloadOpen] = useState(false)
  const [sendOpen, setSendOpen] = useState(false)
  const [showSql, setShowSql] = useState(false)
  const [activeSecret, setActiveSecret] = useState(null)
  const [containerName, setContainerName] = useState('')
  const [sending, setSending] = useState(false)

  const downloadRef = useRef()
  const downloadMenuRef = useRef()
  const sendRef = useRef()
  const sendMenuRef = useRef()
  useOutsideCheckClick([downloadRef], () => setDownloadOpen(false), downloadOpen)
  useOutsideCheckClick([sendRef], () => setSendOpen(false), sendOpen)

  // Menus open upward, above the docked composer. If the trigger is near the
  // top edge, bring the full menu into the transcript's visible scroll area.
  useLayoutEffect(() => {
    const menu = downloadOpen ? downloadMenuRef.current : sendOpen ? sendMenuRef.current : null
    if (!menu) return undefined
    const frame = requestAnimationFrame(() =>
      menu.scrollIntoView({ block: 'nearest', inline: 'nearest' }),
    )
    return () => cancelAnimationFrame(frame)
  }, [downloadOpen, sendOpen])

  // Escape closes the Send-to-CRM modal (unless a send is in flight).
  useEffect(() => {
    if (!activeSecret) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape' && !sending) setActiveSecret(null)
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [activeSecret, sending])

  const question = (turn.question || '').trim()
  const isSaved =
    !!question && savedQuestions.items.some((item) => (item.question || '').trim() === question)

  const onSave = () => {
    if (isSaved) return
    const name = (turn.question || 'Saved question').slice(0, 80)
    saveQuestion(name, turn.question)
    addNotification({
      variant: 'success',
      message: t('Sentinel.V2.actions.savedToastTitle'),
      description: t('Sentinel.V2.actions.savedToastBody'),
    })
  }

  const handleSend = async () => {
    if (!activeSecret || !containerName.trim()) return
    setSending(true)
    // The customer's id may be labelled `customer_id` or just `id` — mirror the
    // resolution ResultTable uses so the CRM export never sends an empty list.
    const cols = (table.columns || []).map((c) => String(c).toLowerCase())
    const byCustomerId = cols.indexOf('customer_id')
    const idIdx = byCustomerId !== -1 ? byCustomerId : cols.indexOf('id')
    const ids = idIdx === -1 ? [] : (table.rows || []).map((r) => r[idIdx])
    const res = await sentinelV2Api.sendTurnToCRM({
      crm_name: activeSecret.service_name,
      secret_id: activeSecret.id,
      container_name: containerName.trim(),
      conversationId,
      turnIndex: turn.turnIndex,
      ids,
    })
    setSending(false)
    const name = capitalizeString(activeSecret.service_name)
    if (res && !res.error) {
      addNotification({
        variant: 'success',
        message: t('Sentinel.V2.crm.successTitle', { name }),
        description: t('Sentinel.V2.crm.successBody', { name }),
      })
      setActiveSecret(null)
      setContainerName('')
    } else {
      addNotification({
        variant: 'error',
        message: t('Sentinel.V2.crm.errorTitle'),
        description: t('Sentinel.V2.crm.errorBody', { name }),
      })
    }
  }

  return (
    <div
      className="mt-4 pt-4 flex items-center gap-2 flex-wrap"
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      <button className="fd-btn fd-btn--sm" onClick={onSave} disabled={isSaved}>
        <i className={isSaved ? 'fa-solid fa-star' : 'fa-regular fa-star'} aria-hidden="true" />
        {isSaved ? t('Sentinel.V2.actions.saved') : t('Sentinel.V2.actions.save')}
      </button>

      <div className="relative" ref={downloadRef}>
        <button className="fd-btn fd-btn--sm" onClick={() => setDownloadOpen((o) => !o)}>
          <i className="fa-solid fa-download" aria-hidden="true" />{' '}
          {t('Sentinel.V2.actions.export')}{' '}
          <i className="fa-solid fa-chevron-down" aria-hidden="true" />
        </button>
        {downloadOpen && (
          <div ref={downloadMenuRef} className={MENU} style={MENU_STYLE}>
            <button
              className={ITEM}
              style={ITEM_STYLE}
              onClick={() => {
                downloadTableCSV(table.columns, table.rows, fileName)
                setDownloadOpen(false)
              }}
            >
              CSV
            </button>
            <button
              className={ITEM}
              style={ITEM_STYLE}
              onClick={() => {
                downloadTableXLSX(table.columns, table.rows, fileName)
                setDownloadOpen(false)
              }}
            >
              Excel
            </button>
          </div>
        )}
      </div>

      {turn.isAudience && supportedCRMs.length > 0 && (
        <div className="relative" ref={sendRef}>
          <button className="fd-btn fd-btn--sm" onClick={() => setSendOpen((o) => !o)}>
            <i className="fa-solid fa-paper-plane" aria-hidden="true" />{' '}
            {t('Sentinel.V2.actions.sendAudience')}{' '}
            <i className="fa-solid fa-chevron-down" aria-hidden="true" />
          </button>
          {sendOpen && (
            <div ref={sendMenuRef} className={MENU} style={MENU_STYLE}>
              {supportedCRMs.map((secret) => (
                <button
                  key={secret.id}
                  className={ITEM}
                  style={ITEM_STYLE}
                  onClick={() => {
                    setActiveSecret(secret)
                    setContainerName('')
                    setSendOpen(false)
                  }}
                >
                  {secret.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {turn.sql && (
        <button className="fd-btn fd-btn--sm fd-btn--ghost" onClick={() => setShowSql((s) => !s)}>
          {showSql ? t('Sentinel.V2.actions.hideSql') : t('Sentinel.V2.actions.viewSql')}
        </button>
      )}

      {showSql && turn.sql && (
        <pre className="fd-sql" style={{ flexBasis: '100%', marginTop: '8px' }}>
          {turn.sql}
        </pre>
      )}

      {activeSecret && (
        <div
          className="fd-modal-backdrop"
          role="presentation"
          onClick={() => !sending && setActiveSecret(null)}
        >
          <div
            className="fd-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sentinel-crm-title"
            style={{ width: '380px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fd-modal__header">
              <h3 id="sentinel-crm-title" className="fd-modal__title">
                {t('Sentinel.V2.crm.title', { name: capitalizeString(activeSecret.service_name) })}
              </h3>
              <button
                className="fd-btn fd-btn--ghost fd-btn--icon fd-btn--sm"
                aria-label={t('Sentinel.V2.close')}
                onClick={() => setActiveSecret(null)}
                disabled={sending}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="fd-field">
              <label className="fd-label" htmlFor="sentinel-crm-container">
                {t('Sentinel.V2.crm.listLabel', {
                  name: capitalizeString(activeSecret.service_name),
                })}
              </label>
              <input
                id="sentinel-crm-container"
                autoFocus
                className="fd-input"
                value={containerName}
                onChange={(e) => setContainerName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder={t('Sentinel.V2.crm.placeholder')}
              />
              <span className="fd-hint">{t('Sentinel.V2.crm.hint')}</span>
            </div>
            <div className="fd-modal__footer">
              <button
                className="fd-btn fd-btn--sm"
                onClick={() => setActiveSecret(null)}
                disabled={sending}
              >
                {t('Sentinel.V2.cancel')}
              </button>
              <button
                className="fd-btn fd-btn--sm fd-btn--primary"
                onClick={handleSend}
                disabled={sending || !containerName.trim()}
              >
                {sending ? t('Sentinel.V2.crm.sending') : t('Sentinel.V2.crm.send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

TurnActions.propTypes = { turn: PropTypes.object.isRequired }
